import React from "react";
import { styled } from "@mui/material/styles";
import Guide from "./components/Guide/Guide";
import NavBar from "../../components/NavBar/NavBar";
import SideBar from "./components/SideBar/SideBar";
import Reports from "./components/Reports/Reports";
import IconLogo from '../../icon.logo.black.png';
import Settings from "./components/Settings/Settings";
import LoadingLogo from '../../icon.logo.green.png';
import ActiveLeads from "./components/ActiveLeads/ActiveLeads";
import GreenIconLogo from '../../icon.logo.green.png';
import Configuration from "./components/Configuration/Configuration";
import PropertySearch from "./components/PropertySearch/PropertySearch";
import { useSelector } from "react-redux";
import MemberPerformance from "./components/MemberPerformance/MemberPerformance";
import MortgageRepository from "./components/MortgageRepository/MortgageRepository";
import RefinancesOverview from "./components/RefinancesOverview/RefinancesOverview";
import PortfolioFinancials from "./components/PortfolioFinancials/PortfolioFinancials";
import RenegotiationsOverview from "./components/RenegotiationsOverview/RenegotiationsOverview";
import LeadGenerationOverview from "./components/LeadGenerationOverview/LeadGenerationOverview";
import { selectUserFullName } from "../../slices/userInfoSlice";
import { selectAppLoaded, selectLoggedOut } from "../../slices/sessionDataSlice";
import { PageContainer, ScreenSaverContainer, ImageElements, FlexWrapper } from "../../StyledComponents";

const Dashboard = ({ teamId, userId, subPage }) => {

  const appLoaded = useSelector(selectAppLoaded)
  const userFullName = useSelector(selectUserFullName)
  const loggedOut = useSelector(selectLoggedOut)

  return (
    <PageContainer className="dashboard">
      {!appLoaded ?
        <ScreenSaverContainer className="screenSaver">
          <LoadingMark src={IconLogo} className="centerLogo opaque" />
          <ImageElements src={LoadingLogo} className="centerLogo loading one" />
          <ImageElements src={LoadingLogo} className="centerLogo loading two" />
          <ImageElements src={LoadingLogo} className="centerLogo loading three" />
          <ImageElements src={LoadingLogo} className="centerLogo loading four" />
          <ImageElements src={LoadingLogo} className="centerLogo loading five" />
        </ScreenSaverContainer>
      : loggedOut ?
        <ScreenSaverContainer className="screenSaver fadeIn">
          <SignOutMessage className="column">
            <SignOutMessageRow>
              <SignOutText>
                Thank you for watching
              </SignOutText>
            </SignOutMessageRow>
          </SignOutMessage>
          <SignedOutMark src={GreenIconLogo} className="centerLogo" />
        </ScreenSaverContainer>
      :
        <ScreenSaverContainer className="fadeOut">
          <LoadingMark src={IconLogo} className="centerLogo opaque" />
          <ImageElements src={LoadingLogo} className="centerLogo loaded one" />
          <ImageElements src={LoadingLogo} className="centerLogo loaded two" />
          <ImageElements src={LoadingLogo} className="centerLogo loaded three" />
          <ImageElements src={LoadingLogo} className="centerLogo loaded four" />
          <ImageElements src={LoadingLogo} className="centerLogo loaded five" />
          <ImageElements src={LoadingLogo} className="centerLogo loaded six" />
          <ImageElements src={LoadingLogo} className="centerLogo loaded seven" />
          <ImageElements src={LoadingLogo} className="centerLogo loaded eight" />
          <ImageElements src={LoadingLogo} className="centerLogo loaded nine" />
          <ImageElements src={LoadingLogo} className="centerLogo loaded ten" />
        </ScreenSaverContainer>
      }
      <NavBar />
      <SideBar userId={userId} />
      <DashboardWorkspace>
        <DashboardContentSurface className="noScroll">
          {subPage === 'refinancesOverview' ?
            <RefinancesOverview teamId={teamId} />
          : subPage === 'renegotiationsOverview' ?
            <RenegotiationsOverview />
          : subPage === 'leadGeneration' ?
            <LeadGenerationOverview />
          : subPage === 'activeLeads' ?
            <ActiveLeads teamId={teamId} userId={userId} />
          : subPage === 'mortgageRepository' ?
            <MortgageRepository userId={userId} teamId={teamId} />
          : subPage === 'portfolioFinancials' ?
            <PortfolioFinancials teamId={teamId} userFullName={userFullName} />
          : subPage === 'memberPerformance' ?
            <MemberPerformance />
          : subPage === 'propertySearch' ?
            <PropertySearch userFullName={userFullName} teamId={teamId} userId={userId} />
          : subPage === 'reports' ?
            <Reports userId={userId} />
          : subPage === 'configuration' ?
            <Configuration />
          : subPage === 'settings' ?
            <Settings teamId={teamId} userFullName={userFullName} userId={userId} />
          : subPage === 'guide' ?
            <Guide />
          :
            <></>
          }
        </DashboardContentSurface>

        <DashboardFooter>
          <FooterLogo src={IconLogo} alt="Lancaster Logo" />
          Lancaster Sweep
        </DashboardFooter>
      </DashboardWorkspace>
    </PageContainer>
  )
}

const DashboardWorkspace = styled("main")({
  gridRow: 2,
  gridColumn: 2,
  position: "relative",
  minHeight: "100%",
  maxHeight: "calc(100vh - 4.5rem)",
  maxWidth: "calc(100vw - 19rem)",
  display: "grid",
  gridTemplateRows: "minmax(0, 1fr) 2.5rem",
  background:
    "linear-gradient(180deg, #f8fafc 0%, #eef3f7 54%, #e7edf4 100%)",
  overflow: "hidden",
});

const DashboardContentSurface = styled("section")({
  position: "relative",
  minWidth: "100%",
  maxWidth: "calc(100vw - 21rem)",
  height: "calc(100vh - 7rem)",
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.72), rgba(255,255,255,0.44))",
  borderTop: "0.1rem solid rgba(148,163,184,0.14)",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.72)",
  overflow: "hidden",
});

const DashboardFooter = styled("footer")({
  height: "2.5rem",
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "0.3rem",
  color: "#64748b",
  background: "rgba(255,255,255,0.74)",
  borderTop: "0.1rem solid rgba(120,139,158,0.22)",
  boxShadow: "0 -0.4rem 1.4rem rgba(71,85,105,0.06)",
  fontSize: "0.9rem",
  fontWeight: 800,
});

const FooterLogo = styled("img")({
  height: "80%",
  width: "auto",
  display: "block",
});

const LoadingMark = styled(ImageElements)({
  height: "40rem",
  width: "40rem",
});

const SignedOutMark = styled(ImageElements)({
  height: "40rem",
  width: "40rem",
  opacity: 0.5,
});

const SignOutMessage = styled(FlexWrapper)({
  margin: "auto",
  padding: "0 0 10rem 0",
  height: "fit-content",
  width: "fit-content",
  zIndex: 100000,
});

const SignOutMessageRow = styled(FlexWrapper)({
  margin: "1rem 0 4.5rem 0",
});

const SignOutText = styled("span")({
  opacity: 0.8,
  fontSize: "3rem",
  color: "#DFDFEB",
  fontWeight: 300,
  fontFamily: "exo-bold",
  margin: "13rem 0 0 60.5rem",
});

export default Dashboard;